/**
 * GTM Event Tracking Constants
 * Centralized constants for Google Tag Manager event tracking to ensure consistency and maintainability
 */

/**
 * Event Categories - High-level grouping of events
 */
export const GTM_EVENT_CATEGORIES = {
    COMMUNICATION_PREFERENCES: 'Communication Preferences',
    SSAP_FORM: 'SSAP Form',
    SSAP_ACTIONS: 'SSAP Actions',
    TOOLTIP: 'Tooltip',
    DASHBOARD: 'Dashboard',
} as const;

/**
 * Event Actions - Specific actions within categories
 */
export const GTM_EVENT_ACTIONS = {
    // Communication Preferences Actions
    ADD_EMAIL_CLICKED: 'Add Email Clicked',
    ADDRESS_UPDATE_CLICKED: 'Address Update Clicked',
    CHANGE_EMAIL_CLICKED: 'Change Email Clicked',
    VERIFY_EMAIL_CLICKED: 'Verify Email Clicked',
    CHANGE_PHONE_CLICKED: 'Change Phone Clicked',
    VERIFY_PHONE_CLICKED: 'Verify Phone Clicked',
    VERIFY_SMS_CLICKED: 'Verify SMS Clicked',
    
    // SSAP Actions
    FORM_INTERACTION: 'Form Interaction',
    BUTTON_CLICK: 'Button Click',
    
    // Tooltip Actions
    HOVER: 'Hover',
    
    // Dashboard Actions
    PAGE_LOAD: 'Page Load',
} as const;

/**
 * Event Labels - Descriptive labels for events
 */
export const GTM_EVENT_LABELS = {
    // Communication Preferences Labels
    OPEN_MAILING_ADDRESS_UPDATE_MODAL: 'Open mailing address update modal',
    OPEN_EMAIL_VERIFICATION_MODAL: 'Open email verification modal',
    OPEN_PHONE_VERIFICATION_MODAL: 'Open phone verification modal',
    OPEN_SMS_VERIFICATION_MODAL: 'Open SMS verification modal',
    CHANGE_EXISTING_EMAIL: 'Change existing email',
    ADD_NEW_EMAIL: 'Add new email',
    CHANGE_EXISTING_PHONE: 'Change existing phone',
    ADD_NEW_PHONE: 'Add new phone',
    
    // SSAP Form Labels
    ASSISTANCE_FORM: 'Assistance Form',
    FINANCIAL_ASSISTANCE_FORM_COVERAGE: 'Financial Assistance Form - Coverage',
    FINANCIAL_ASSISTANCE_FORM_REPRESENTATIVE: 'Financial Assistance Form - Representative',
    ADDITIONAL_INFO_FORM: 'Additional Info Form',
    ETHNICITY_RACE_FORM: 'Ethnicity Race Form',
    
    // SSAP Action Labels
    PRINT_BUTTON: 'Print Button',
    DOWNLOAD_BUTTON: 'Download Button',
} as const;

/**
 * Helper function to get dynamic event labels based on conditions
 */
export const getGTMEventLabel = {
    emailAction: (hasEmail: boolean): string => 
        hasEmail ? GTM_EVENT_LABELS.CHANGE_EXISTING_EMAIL : GTM_EVENT_LABELS.ADD_NEW_EMAIL,
    
    phoneAction: (hasPhone: boolean): string => 
        hasPhone ? GTM_EVENT_LABELS.CHANGE_EXISTING_PHONE : GTM_EVENT_LABELS.ADD_NEW_PHONE,
};

/**
 * Ticket Shortcut Key GTM Actions
 * Maps keyboard shortcuts to GTM action names for ticket management pages.
 *
 * Only shortcuts handled in React (SideNavIncludes.jsx) use this map at runtime.
 * The remaining shortcuts are handled in JSP files with inline pushShortcutGTM() calls:
 *   - ticketQuickActionBar.jsp: i, r, m
 *   - ticketdetail.jsp: e, w, t, n, p
 *   - ticketsummary.jsp: s, h, c, a, k, l, m, g, v
 */
export const TICKET_SHORTCUT_GTM_ACTIONS = {
    s: 'ticket_summary',
    h: 'ticket_history',
    c: 'ticket_comments',
    a: 'ticket_attachments',
    k: 'ticket_tasks',
    e: 'ticket_edit',
    w: 'ticket_edit_workgroup_assigned',
    t: 'ticket_edit_ticket_type',
    n: 'ticket_edit_assignee',
    p: 'ticket_access_member_profile',
    i: 'ticket_awaiting_internal',
    r: 'ticket_awaiting_requestor',
    m: 'ticket_mark_as_complete',
    l: 'ticket_cancel',
    g: 'ticket_send_message',
    v: 'ticket_view_member_account',
} as const;

/**
 * Recommended metadata fields for different event types
 * Use these fields consistently to improve analytics segmentation
 */
export const GTM_METADATA_GUIDELINES = {
    /**
     * UI Component Events (buttons, links, tooltips)
     * Recommended: elementId, dataAutomationId, componentName
     */
    UI_COMPONENT: ['elementId', 'dataAutomationId', 'componentName'],
    
    /**
     * Form Events
     * Recommended: elementId, componentName
     */
    FORM: ['elementId', 'componentName'],
    
    /**
     * Page/View Events
     * Recommended: pageCategory, componentName
     */
    PAGE_VIEW: ['pageCategory', 'componentName'],
    
    /**
     * Tooltip Events
     * Recommended: elementId, dataAutomationId, content, componentName
     */
    TOOLTIP: ['elementId', 'dataAutomationId', 'content', 'componentName'],
} as const;
